'use strict';

/**
 * Scales width x height down to fit within the maximum, keeping proportions. Never scales up.
 */
const fitWithin = (width, height, maxWidth, maxHeight) => {
	const ratio = (max, size) => (max !== undefined && max > 0 ? max / size : 1);
	const scale = Math.min(1, ratio(maxWidth, width), ratio(maxHeight, height));
	return {
		width: Math.max(1, Math.round(width * scale)),
		height: Math.max(1, Math.round(height * scale))
	};
};

/**
 * What Elm sends through the crop port.
 * @typedef {object} CropRequest
 * @property {string} cropperId
 * @property {string} fileSuffix - original file name without extension
 * @property {number} [maxWidth] - missing means no limit
 * @property {number} [maxHeight]
 */

const toBlob = canvas =>
	new Promise((resolve, reject) => {
		canvas.toBlob(blob => (blob ? resolve(blob) : reject(new Error('Empty canvas'))));
	});

/**
 * Port handler that captures the cropped image, scales it down to the network maximum and
 * sends it to Elm as a PNG file, or null on failure so Elm stops waiting.
 */
const cropImage = (pmtElmService, { id, port, elmModule, capturedPort }, doc) => {
	const send = file => pmtElmService.sendData(elmModule, capturedPort, file, true);

	/** @param {CropRequest} request */
	const capture = async ({ cropperId, fileSuffix, maxWidth, maxHeight }) => {
		const cropper = doc.getElementById(cropperId);
		if (!cropper) {
			throw new Error('Cropper not found: ' + cropperId);
		}

		const captured = await cropper.captureImage();
		const target = fitWithin(captured.width, captured.height, maxWidth, maxHeight);

		let canvas = captured;
		if (target.width !== captured.width || target.height !== captured.height) {
			canvas = doc.createElement('canvas');
			canvas.width = target.width;
			canvas.height = target.height;
			const context = canvas.getContext('2d');
			if (!context) {
				throw new Error('Could not create a canvas to scale the image');
			}
			context.drawImage(captured, 0, 0, target.width, target.height);
		}

		const blob = await toBlob(canvas);
		return new File([blob], `cropped-${fileSuffix}.png`, { type: blob.type });
	};

	return {
		id,
		port,
		f: args =>
			capture(args).then(send, error => {
				console.error('Failed to capture cropped image', error);
				send(null);
			})
	};
};

module.exports = {
	cropImage,
	fitWithin
};
